import * as React from 'react';

import {
	ItemsProviderContext,
	ItemsProviderContextActions,
	useItemsProviderContext,
	WithKey,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import { useItemProviderContext, ItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import useRequest from '@common/react/hooks/useRequest';
import { BaseParams } from '@common/typescript/objects/BaseParams';
import { List } from '@common/typescript/objects/List';

export type KeysType<TFilter extends BaseParams = BaseParams> = Partial<{ [key in keyof TFilter]: TFilter[key] | string | number }>;

interface RenderProps<T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TFilterValues = BaseParams,
	TResponse extends List<T> = List<T>> extends ItemsProviderContext<T,
		TFilter,
		TResponse,
		ItemsProviderContextActions<T, TFilter, TResponse>
	> {
		keys: KeysType<TFilter>;
		setKeys: React.Dispatch<React.SetStateAction<KeysType<TFilter>>>;
		values: TFilterValues;
		setValues: React.Dispatch<React.SetStateAction<TFilterValues>>;
	}

interface BaseFilterComponentProps<T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TFilterValues = BaseParams,
	TResponse extends List<T> = List<T>> {
	/**
	 * render function
	 */
	render?: (values: RenderProps<T, TFilter, TFilterValues, TResponse>) => React.ReactNode;
	/**
	 * function to get init values from ssp
	 * @param sspContextState
	 */
	getInitValues?: (sspContextState: ItemProviderContext<any>) => TFilterValues;
	/**
	 * initValues
	 */
	initValues?: TFilterValues;
	/**
	 * load filters request
	 */
	loadFiltersRequest?: string;
	/**
	 * function to handle error
	 */
	onFilterLoadError?: (e) => void;
	children?: React.ReactNode | ((values: RenderProps<T, TFilter, TFilterValues, TResponse>) => React.ReactNode);
}

const defaultGetInitValues = (context) => context?.state?.item?.filterProps;

/**
 * BaseFilterComponent component.
 *
 * component to get ItemsProvider context and load values for filters
 * (filters usually only store id, but the filterComponent itself needs text and a request is made to get the names)
 *
 * @typeParam T - T Any {WithKey}
 * @typeParam TFilter
 * @typeParam TFilterValues
 * @typeParam TResponse
 * @param props - BaseFilterComponentProps<T, TFilter, TFilterValues, TResponse>
 * @returns React.ReactElement
 */
const BaseFilterComponent: <T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TFilterValues = BaseParams,
	TResponse extends List<T> = List<T>>(
	p: BaseFilterComponentProps<T, TFilter, TFilterValues, TResponse>,
) => React.ReactElement<T> = <T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TFilterValues = BaseParams,
	TResponse extends List<T> = List<T>>(props: BaseFilterComponentProps<T, TFilter, TFilterValues, TResponse>) => {
	const {
		initValues: initValuesProps, getInitValues = defaultGetInitValues, loadFiltersRequest, render, children,
	} = props;

	const request = useRequest();
	const { state, actions } = useItemsProviderContext<T, TFilter, TResponse>();
	const sspContext = useItemProviderContext<any>(false);

	const [keys, setKeys] = React.useState<any>({ key: 'default' });
	const [values, setValues] = React.useState<TFilterValues>(() => initValuesProps || getInitValues(sspContext));

	React.useEffect(() => {
		if (!values && loadFiltersRequest) {
			request<TFilterValues, TFilter>(loadFiltersRequest, state.filters)
				.then((res) => {
					setValues((prev) => ({ ...prev, ...res }));
					setKeys({ key: 'loadValues' });
				})
				.catch((e) => props.onFilterLoadError && props.onFilterLoadError(e));
		}
	}, []);

	const resProps = {
		state,
		actions,
		keys,
		setKeys,
		values,
		setValues,
	};

	return (
		<>
			{typeof children !== 'undefined'
				? typeof children === 'function' ? children(resProps) : children
				: render?.(resProps)}
		</>
	);
};

export default BaseFilterComponent;
